import moment from "moment";
import { DateGapStatement } from "../../../../docuclipper/api";

// Pure helpers for the Date Gaps coverage view. Factored out of index.tsx so
// the state-machine logic (cell color, CSV serialization, month range
// generation) can be unit-tested without mounting the Redux-connected
// component.

export type CellState = "missing" | "unreconciled" | "reconciled" | "txOnly";

export const CELL_COLORS: Record<CellState, string> = {
  missing: "#f8d7da",
  unreconciled: "#fff3cd",
  reconciled: "#d4edda",
  txOnly: "#d1ecf1",
};

export const CELL_BORDER: Record<CellState, string> = {
  missing: "#f5c2c7",
  unreconciled: "#ffe69c",
  reconciled: "#a3d9a5",
  txOnly: "#bee5eb",
};

export const CELL_LEGEND: Array<{ state: CellState; label: string; body: string }> = [
  { state: "reconciled", label: "Reconciled", body: "Statement present, balances match" },
  { state: "unreconciled", label: "Statement, not reconciled", body: "Statement imported but reconciliation not confirmed" },
  { state: "txOnly", label: "Transactions only", body: "Transaction rows exist but no statement metadata — partial coverage" },
  { state: "missing", label: "Missing", body: "No statement, no transactions — gap in your workpapers" },
];

export function stateLabel(s: CellState): string {
  return CELL_LEGEND.find((l) => l.state === s)!.label;
}

export function monthRange(start: string, end: string): string[] {
  if (!start || !end) return [];
  const s = moment(start + "-01");
  const e = moment(end + "-01");
  if (!s.isValid() || !e.isValid() || s.isAfter(e)) return [];
  const out: string[] = [];
  const cur = s.clone();
  while (cur.isSameOrBefore(e, "month")) {
    out.push(cur.format("YYYY-MM"));
    cur.add(1, "month");
  }
  return out;
}

export interface CellInfo {
  state: CellState;
  statements: DateGapStatement[];
  hasTx: boolean;
}

export function computeCellInfo(
  account: string,
  month: string,
  dateGaps: Record<string, Record<string, boolean>>,
  statementsByAccount: Record<string, DateGapStatement[]>
): CellInfo {
  const hasTx = !!(dateGaps[account] && dateGaps[account][month]);
  const touching = (statementsByAccount[account] || []).filter((s) => {
    if (!s.startDate || !s.endDate) return false;
    const sM = String(s.startDate).slice(0, 7);
    const eM = String(s.endDate).slice(0, 7);
    return month >= sM && month <= eM;
  });
  if (touching.length > 0) {
    const anyReconciled = touching.some(
      (s) => s.reconciliationStatus === "reconciled" || s.reconciliationStatus === "matched"
    );
    return {
      state: anyReconciled ? "reconciled" : "unreconciled",
      statements: touching,
      hasTx,
    };
  }
  if (hasTx) return { state: "txOnly", statements: [], hasTx };
  return { state: "missing", statements: [], hasTx };
}

export function groupStatementsByAccount(
  statements: DateGapStatement[]
): Record<string, DateGapStatement[]> {
  const map: Record<string, DateGapStatement[]> = {};
  statements.forEach((s) => {
    if (!s.accountName) return;
    if (!map[s.accountName]) map[s.accountName] = [];
    map[s.accountName].push(s);
  });
  return map;
}

export interface PerAccountSummary {
  account: string;
  coveragePct: number;
  reconciledStatements: number;
  statements: number;
}

export function buildCoverageCsv(
  accounts: string[],
  months: string[],
  dateGaps: Record<string, Record<string, boolean>>,
  statementsByAccount: Record<string, DateGapStatement[]>,
  perAccount: PerAccountSummary[]
): string {
  const headers = ["Account", ...months, "Coverage %", "Reconciled / Statements"];
  const lines: string[] = [headers.join(",")];
  accounts.forEach((a) => {
    const perAcc = perAccount.find((p) => p.account === a);
    const cells = months.map((m) =>
      stateLabel(computeCellInfo(a, m, dateGaps, statementsByAccount).state)
    );
    const cov = perAcc ? `${perAcc.coveragePct}%` : "";
    const rec = perAcc ? `${perAcc.reconciledStatements}/${perAcc.statements}` : "";
    const row = [a, ...cells, cov, rec].map((v) => `"${String(v).replace(/"/g, '""')}"`);
    lines.push(row.join(","));
  });
  return lines.join("\n");
}
